import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { attendanceAPI, employeeAPI, holidayAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Users, TrendingUp, Clock, AlertTriangle, RefreshCw, Building2,
  Check, X, Send, FileText, Shield, ArrowRight, Undo2, Settings, Save, Calendar, Plus, Trash2, Hand
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Switch } from '../components/ui/switch';
import { formatDate } from '../lib/utils';

export default function AttendanceAdminPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isHR = ['super_admin', 'admin', 'hr'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  
  // Overview state
  const [loading, setLoading] = useState(true);
  const [orgSummary, setOrgSummary] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('all');
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from: firstDay.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  });
  
  // Exception management state
  const [exceptions, setExceptions] = useState([]);
  const [pendingExceptions, setPendingExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionSubmitting, setExceptionSubmitting] = useState(false);
  
  // Approval dialog state
  const [approvalDialog, setApprovalDialog] = useState({ open: false, exception: null, action: null });
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Deductions state
  const [deductions, setDeductions] = useState([]);
  const [deductionsLoading, setDeductionsLoading] = useState(false);
  const [deductionFilters, setDeductionFilters] = useState({ status: '', employee_id: '' });
  
  // Employees list for selection
  const [employees, setEmployees] = useState([]);
  
  // Configuration state (Settings tab)
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configForm, setConfigForm] = useState({
    full_day_hours: 8.5,
    half_day_min_hours: 5,
    lunch_break_minutes: 30,
    max_break_minutes: 30,
    compensation_hours_per_half_day: 4,
    single_punch_deduction: true,
    check_holidays: true,
    grace_period_minutes: 0
  });
  
  // Regularization state
  const [regularizations, setRegularizations] = useState([]);
  const [regularizationsLoading, setRegularizationsLoading] = useState(false);
  const [pendingHRRegularizations, setPendingHRRegularizations] = useState([]);
  const [pendingAdminRegularizations, setPendingAdminRegularizations] = useState([]);
  const [regularizationDialog, setRegularizationDialog] = useState({ open: false, request: null, action: null });
  const [regularizationComments, setRegularizationComments] = useState('');
  const [regularizationProcessing, setRegularizationProcessing] = useState(false);
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [holidaySubmitting, setHolidaySubmitting] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    type: 'company',
    description: ''
  });
  
  // Attendance issues state
  const [attendanceIssues, setAttendanceIssues] = useState(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Fetch overview data
  const fetchOverviewData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceAPI.getOrgSummary(
        dateRange.from,
        dateRange.to,
        selectedDept !== 'all' ? selectedDept : undefined
      );
      setOrgSummary(res.data);
      const depts = [...new Set(res.data.employees?.map(e => e.department).filter(Boolean))];
      setDepartments(depts);
      
      // Fetch attendance issues summary
      try {
        const issuesRes = await attendanceAPI.getIssuesSummary(
          dateRange.from,
          dateRange.to,
          selectedDept !== 'all' ? selectedDept : undefined
        );
        setAttendanceIssues(issuesRes.data);
      } catch (issErr) {
        console.error('Failed to fetch attendance issues:', issErr);
      }
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedDept]);

  // Fetch exceptions
  const fetchExceptions = useCallback(async () => {
    setExceptionsLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        attendanceAPI.getExceptions({ limit: 50 }),
        isSuperAdmin ? attendanceAPI.getPendingExceptions() : Promise.resolve({ data: { exceptions: [] } })
      ]);
      setExceptions(allRes.data.exceptions || []);
      setPendingExceptions(pendingRes.data.exceptions || []);
    } catch (error) {
      console.error('Failed to fetch exceptions:', error);
    } finally {
      setExceptionsLoading(false);
    }
  }, [isSuperAdmin]);

  // Fetch deductions
  const fetchDeductions = useCallback(async () => {
    setDeductionsLoading(true);
    try {
      const params = { limit: 100 };
      if (deductionFilters.status) params.status = deductionFilters.status;
      if (deductionFilters.employee_id) params.employee_id = deductionFilters.employee_id;
      
      const res = await attendanceAPI.getDeductions(params);
      setDeductions(res.data.deductions || []);
    } catch (error) {
      console.error('Failed to fetch deductions:', error);
    } finally {
      setDeductionsLoading(false);
    }
  }, [deductionFilters]);

  // Fetch employees for dropdown
  const fetchEmployees = async () => {
    try {
      const res = await employeeAPI.getAll({ status: 'active' });
      setEmployees(res.data.employees || res.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };
  
  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const res = await attendanceAPI.getConfig();
      setConfig(res.data);
      setConfigForm({
        full_day_hours: res.data.full_day_hours || 8.5,
        half_day_min_hours: res.data.half_day_min_hours || 5,
        lunch_break_minutes: res.data.lunch_break_minutes || 30,
        max_break_minutes: res.data.max_break_minutes || 30,
        compensation_hours_per_half_day: res.data.compensation_hours_per_half_day || 4,
        single_punch_deduction: res.data.single_punch_deduction ?? true,
        check_holidays: res.data.check_holidays ?? true,
        grace_period_minutes: res.data.grace_period_minutes || 0
      });
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setConfigLoading(false);
    }
  }, []);
  
  // Save configuration
  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      await attendanceAPI.updateConfig(configForm);
      toast.success('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setConfigSaving(false);
    }
  };
  
  // Fetch holidays
  const fetchHolidays = useCallback(async () => {
    setHolidaysLoading(true);
    try {
      const res = await holidayAPI.getAll({ year: selectedYear });
      setHolidays(res.data || []);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setHolidaysLoading(false);
    }
  }, [selectedYear]);
  
  // Handle holiday creation
  const handleCreateHoliday = async (e) => {
    e.preventDefault();
    setHolidaySubmitting(true);
    try {
      await holidayAPI.create({
        ...holidayForm,
        date: new Date(holidayForm.date).toISOString()
      });
      toast.success('Holiday added successfully');
      setShowHolidayDialog(false);
      setHolidayForm({ name: '', date: '', type: 'company', description: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setHolidaySubmitting(false);
    }
  };
  
  // Handle holiday deletion
  const handleDeleteHoliday = async (id) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await holidayAPI.delete(id);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };
  
  // Fetch regularizations
  const fetchRegularizations = useCallback(async () => {
    setRegularizationsLoading(true);
    try {
      const [hrRes, adminRes, allRes] = await Promise.all([
        attendanceAPI.getPendingHRRequests(),
        isSuperAdmin ? attendanceAPI.getPendingAdminRequests() : Promise.resolve({ data: { requests: [] } }),
        attendanceAPI.getAllRegularizationRequests({ limit: 50 })
      ]);
      
      setPendingHRRegularizations(hrRes.data.requests || []);
      setPendingAdminRegularizations(adminRes.data.requests || []);
      setRegularizations(allRes.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch regularizations:', error);
    } finally {
      setRegularizationsLoading(false);
    }
  }, [isSuperAdmin]);
  
  // Handle HR approval of regularization
  const handleHRApproveRegularization = async () => {
    if (!regularizationDialog.request) return;
    
    setRegularizationProcessing(true);
    try {
      await attendanceAPI.hrApproveRequest(regularizationDialog.request.id, { comments: regularizationComments });
      toast.success('Request forwarded to Super Admin');
      setRegularizationDialog({ open: false, request: null, action: null });
      setRegularizationComments('');
      fetchRegularizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setRegularizationProcessing(false);
    }
  };
  
  // Handle HR rejection of regularization
  const handleHRRejectRegularization = async () => {
    if (!regularizationDialog.request || !regularizationComments.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setRegularizationProcessing(true);
    try {
      await attendanceAPI.hrRejectRequest(regularizationDialog.request.id, { reason: regularizationComments });
      toast.success('Request rejected');
      setRegularizationDialog({ open: false, request: null, action: null });
      setRegularizationComments('');
      fetchRegularizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRegularizationProcessing(false);
    }
  };
  
  // Handle Admin approval of regularization
  const handleAdminApproveRegularization = async () => {
    if (!regularizationDialog.request) return;
    
    setRegularizationProcessing(true);
    try {
      await attendanceAPI.adminApproveRequest(regularizationDialog.request.id, { comments: regularizationComments });
      toast.success('Request approved - attendance regularized');
      setRegularizationDialog({ open: false, request: null, action: null });
      setRegularizationComments('');
      fetchRegularizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setRegularizationProcessing(false);
    }
  };
  
  // Handle Admin rejection of regularization
  const handleAdminRejectRegularization = async () => {
    if (!regularizationDialog.request || !regularizationComments.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setRegularizationProcessing(true);
    try {
      await attendanceAPI.adminRejectRequest(regularizationDialog.request.id, { reason: regularizationComments });
      toast.success('Request rejected');
      setRegularizationDialog({ open: false, request: null, action: null });
      setRegularizationComments('');
      fetchRegularizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRegularizationProcessing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverviewData();
    } else if (activeTab === 'exceptions') {
      fetchExceptions();
    } else if (activeTab === 'deductions') {
      fetchDeductions();
      fetchEmployees();
    } else if (activeTab === 'settings') {
      fetchConfig();
    } else if (activeTab === 'regularizations') {
      fetchRegularizations();
    } else if (activeTab === 'holidays') {
      fetchHolidays();
    }
  }, [activeTab, fetchOverviewData, fetchExceptions, fetchDeductions, fetchConfig, fetchRegularizations, fetchHolidays]);

  // Exception creation
  const handleCreateException = async () => {
    if (!selectedDeduction || !exceptionReason.trim()) {
      toast.error('Please select a deduction and provide a reason');
      return;
    }
    
    setExceptionSubmitting(true);
    try {
      await attendanceAPI.createException({
        employee_id: selectedDeduction.employee_id,
        deduction_id: selectedDeduction.id,
        date: selectedDeduction.date,
        reason: exceptionReason
      });
      
      toast.success('Exception request submitted for Super Admin approval');
      setShowExceptionDialog(false);
      setSelectedDeduction(null);
      setExceptionReason('');
      fetchDeductions();
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create exception');
    } finally {
      setExceptionSubmitting(false);
    }
  };

  // Exception approval/rejection
  const handleApproveException = async () => {
    if (!approvalDialog.exception) return;
    
    setProcessing(true);
    try {
      await attendanceAPI.approveException(approvalDialog.exception.id, approvalComments);
      toast.success('Exception approved - Leave balance restored');
      setApprovalDialog({ open: false, exception: null, action: null });
      setApprovalComments('');
      fetchExceptions();
      fetchDeductions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve exception');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectException = async () => {
    if (!approvalDialog.exception || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await attendanceAPI.rejectException(approvalDialog.exception.id, rejectionReason);
      toast.success('Exception rejected');
      setApprovalDialog({ open: false, exception: null, action: null });
      setRejectionReason('');
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject exception');
    } finally {
      setProcessing(false);
    }
  };

  // Run compensation check
  const handleRunCompensation = async () => {
    try {
      const res = await attendanceAPI.runMonthlyCompensation();
      toast.success(`Compensation check complete: ${res.data.compensations_applied} employees processed`);
      fetchDeductions();
    } catch (error) {
      toast.error('Failed to run compensation check');
    }
  };

  // Send daily alerts
  const handleSendDailyAlerts = async () => {
    try {
      const res = await attendanceAPI.sendDailyAlerts();
      toast.success(`Sent ${res.data.alerts_sent} short attendance alerts`);
    } catch (error) {
      toast.error('Failed to send alerts');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      deducted: 'bg-orange-100 text-orange-800',
      reversed_exception: 'bg-blue-100 text-blue-800',
      reversed_compensation: 'bg-emerald-100 text-emerald-800',
      unpaid_leave: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  if (!isHR) {
    return (
      <div className="p-8 text-center">
        <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-muted-foreground">This page is only available to HR and Admin users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Attendance Administration
          </h1>
          <p className="text-muted-foreground">
            Manage attendance records, exceptions, and deductions
          </p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <>
              <Button variant="outline" onClick={handleSendDailyAlerts}>
                <Send className="h-4 w-4 mr-2" />
                Send Alerts
              </Button>
              <Button variant="outline" onClick={handleRunCompensation}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Run Compensation
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deductions" className="flex items-center gap-2">
            Deductions
            {deductions.filter(d => d.status === 'deducted').length > 0 && (
              <Badge variant="secondary" className="bg-orange-500 text-white">
                {deductions.filter(d => d.status === 'deducted').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="flex items-center gap-2">
            Exceptions
            {isSuperAdmin && pendingExceptions.length > 0 && (
              <Badge variant="destructive">{pendingExceptions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="regularizations" className="flex items-center gap-2">
            Regularizations
            {(pendingHRRegularizations.length > 0 || pendingAdminRegularizations.length > 0) && (
              <Badge variant="secondary" className="bg-blue-500 text-white">
                {pendingHRRegularizations.length + pendingAdminRegularizations.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Holidays
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {departments.length > 0 && (
              <Select value={selectedDept} onValueChange={setSelectedDept}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-40"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-40"
            />
            <Button variant="outline" onClick={fetchOverviewData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Summary Cards */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      <span className="text-2xl font-bold">{orgSummary?.total_employees || 0}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Total Employees</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      <span className="text-2xl font-bold">{orgSummary?.average_attendance_rate || 0}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Avg Attendance Rate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-purple-500" />
                      <span className="text-2xl font-bold">{orgSummary?.average_hours || 0}h</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Avg Hours/Day</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      <span className="text-2xl font-bold">{orgSummary?.low_attendance_count || 0}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Below Threshold</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Attendance Issues Cards */}
              {attendanceIssues && attendanceIssues.total_issues > 0 && (
                <Alert className="border-amber-500 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">Attendance Issues Detected</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                      <div className="flex items-center gap-2">
                        <Hand className="h-4 w-4" />
                        <span className="font-medium">{attendanceIssues.single_punch_count}</span>
                        <span className="text-sm">Single Punch Days</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-medium">{attendanceIssues.excessive_break_count}</span>
                        <span className="text-sm">Excessive Break Days</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">{attendanceIssues.short_day_count || 0}</span>
                        <span className="text-sm">Short Days</span>
                      </div>
                    </div>
                    
                    {/* Employees with Issues */}
                    {attendanceIssues.employees_with_issues?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-amber-300">
                        <p className="text-sm font-medium mb-2">Top Employees with Issues:</p>
                        <div className="flex flex-wrap gap-2">
                          {attendanceIssues.employees_with_issues.slice(0, 5).map((emp) => (
                            <Badge key={emp.employee_id} variant="outline" className="bg-white text-amber-800 border-amber-400">
                              {emp.employee_name}: {emp.single_punch_days + emp.excessive_break_days + (emp.short_days || 0)} issues
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Employee Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Employee Attendance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Department</TableHead>
                          <TableHead>Present</TableHead>
                          <TableHead>Half Days</TableHead>
                          <TableHead>Leave</TableHead>
                          <TableHead>Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgSummary?.employees?.map((emp) => (
                          <TableRow key={emp.employee_id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell>{emp.department || '-'}</TableCell>
                            <TableCell>{emp.days_present || 0}</TableCell>
                            <TableCell>{emp.days_half_day || 0}</TableCell>
                            <TableCell>{emp.days_leave || 0}</TableCell>
                            <TableCell>
                              <Badge variant={emp.attendance_rate >= 80 ? 'default' : 'destructive'}>
                                {emp.attendance_rate || 0}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Deductions Tab */}
        <TabsContent value="deductions" className="space-y-4">
          {/* Info Banner */}
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertTitle>Attendance-Based Deductions</AlertTitle>
            <AlertDescription>
              These are half-day leave deductions from biometric attendance imports. 
              HR can request exceptions (requires Super Admin approval). 
              Employees can compensate by working 4 extra hours per half-day.
            </AlertDescription>
          </Alert>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Select 
              value={deductionFilters.status} 
              onValueChange={(v) => setDeductionFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="deducted">Active</SelectItem>
                <SelectItem value="reversed_exception">Reversed (Exception)</SelectItem>
                <SelectItem value="reversed_compensation">Reversed (Compensation)</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={deductionFilters.employee_id}
              onValueChange={(v) => setDeductionFilters(prev => ({ ...prev, employee_id: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchDeductions}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Deductions Table */}
          <Card>
            <CardContent className="pt-4">
              {deductionsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : deductions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No deductions found
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Hours Worked</TableHead>
                        <TableHead>Deduction</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deductions.map((ded) => (
                        <TableRow key={ded.id}>
                          <TableCell className="font-medium">{ded.employee_name}</TableCell>
                          <TableCell>{ded.date}</TableCell>
                          <TableCell className="font-mono">{ded.hours_worked}h</TableCell>
                          <TableCell>{ded.amount} day</TableCell>
                          <TableCell>{getStatusBadge(ded.status)}</TableCell>
                          <TableCell>
                            {ded.status === 'deducted' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedDeduction(ded);
                                  setShowExceptionDialog(true);
                                }}
                              >
                                <Undo2 className="h-4 w-4 mr-1" />
                                Request Exception
                              </Button>
                            )}
                            {ded.status.startsWith('reversed') && (
                              <span className="text-sm text-green-600">Restored</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exceptions Tab */}
        <TabsContent value="exceptions" className="space-y-4">
          {/* Pending Approvals for Super Admin */}
          {isSuperAdmin && pendingExceptions.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Pending Approvals ({pendingExceptions.length})
                </CardTitle>
                <CardDescription>These exception requests require your approval</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingExceptions.map((exc) => (
                      <TableRow key={exc.id}>
                        <TableCell className="font-medium">{exc.employee_name}</TableCell>
                        <TableCell>{exc.date}</TableCell>
                        <TableCell className="font-mono">{exc.hours_worked}h</TableCell>
                        <TableCell>{exc.requested_by_name}</TableCell>
                        <TableCell className="max-w-xs truncate">{exc.reason}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => setApprovalDialog({ open: true, exception: exc, action: 'approve' })}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setApprovalDialog({ open: true, exception: exc, action: 'reject' })}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* All Exceptions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Exception History</CardTitle>
            </CardHeader>
            <CardContent>
              {exceptionsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : exceptions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No exception requests yet
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Requested By</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Processed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exceptions.map((exc) => (
                        <TableRow key={exc.id}>
                          <TableCell className="font-medium">{exc.employee_name}</TableCell>
                          <TableCell>{exc.date}</TableCell>
                          <TableCell>{exc.requested_by_name}</TableCell>
                          <TableCell className="max-w-xs truncate">{exc.reason}</TableCell>
                          <TableCell>{getStatusBadge(exc.status)}</TableCell>
                          <TableCell>
                            {exc.status === 'approved' && exc.approved_by_name}
                            {exc.status === 'rejected' && exc.rejected_by_name}
                            {exc.status === 'pending' && '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Settings Tab */}
        {isSuperAdmin && (
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Attendance Rules Configuration
                </CardTitle>
                <CardDescription>
                  Configure attendance thresholds, break time limits, and deduction rules.
                  Changes will apply to all future attendance imports.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {configLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Work Hours Configuration */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="full_day_hours">Full Day Hours</Label>
                        <Input
                          id="full_day_hours"
                          type="number"
                          step="0.5"
                          min="1"
                          max="12"
                          value={configForm.full_day_hours}
                          onChange={(e) => setConfigForm(prev => ({ ...prev, full_day_hours: parseFloat(e.target.value) }))}
                        />
                        <p className="text-xs text-muted-foreground">
                          Minimum hours required for a full working day (default: 8.5)
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="half_day_min_hours">Half Day Minimum Hours</Label>
                        <Input
                          id="half_day_min_hours"
                          type="number"
                          step="0.5"
                          min="1"
                          max="8"
                          value={configForm.half_day_min_hours}
                          onChange={(e) => setConfigForm(prev => ({ ...prev, half_day_min_hours: parseFloat(e.target.value) }))}
                        />
                        <p className="text-xs text-muted-foreground">
                          Minimum hours to be considered half day instead of absent (default: 5)
                        </p>
                      </div>
                    </div>
                    
                    {/* Break Time Configuration */}
                    <div className="border-t pt-6">
                      <h4 className="font-medium mb-4">Break Time Rules</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="lunch_break_minutes">Standard Lunch Break (minutes)</Label>
                          <Input
                            id="lunch_break_minutes"
                            type="number"
                            min="0"
                            max="120"
                            value={configForm.lunch_break_minutes}
                            onChange={(e) => setConfigForm(prev => ({ ...prev, lunch_break_minutes: parseInt(e.target.value) }))}
                          />
                          <p className="text-xs text-muted-foreground">
                            Standard lunch break duration (default: 30 minutes)
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="max_break_minutes">Maximum Extra Break (minutes)</Label>
                          <Input
                            id="max_break_minutes"
                            type="number"
                            min="0"
                            max="120"
                            value={configForm.max_break_minutes}
                            onChange={(e) => setConfigForm(prev => ({ ...prev, max_break_minutes: parseInt(e.target.value) }))}
                          />
                          <p className="text-xs text-muted-foreground">
                            Extra break time allowed before half-day deduction (default: 30 minutes)
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Compensation Configuration */}
                    <div className="border-t pt-6">
                      <h4 className="font-medium mb-4">Compensation Rules</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="compensation_hours_per_half_day">Compensation Hours per Half-Day</Label>
                          <Input
                            id="compensation_hours_per_half_day"
                            type="number"
                            step="0.5"
                            min="1"
                            max="8"
                            value={configForm.compensation_hours_per_half_day}
                            onChange={(e) => setConfigForm(prev => ({ ...prev, compensation_hours_per_half_day: parseFloat(e.target.value) }))}
                          />
                          <p className="text-xs text-muted-foreground">
                            Extra hours required to reverse one half-day deduction (default: 4)
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="grace_period_minutes">Grace Period (minutes)</Label>
                          <Input
                            id="grace_period_minutes"
                            type="number"
                            min="0"
                            max="60"
                            value={configForm.grace_period_minutes}
                            onChange={(e) => setConfigForm(prev => ({ ...prev, grace_period_minutes: parseInt(e.target.value) }))}
                          />
                          <p className="text-xs text-muted-foreground">
                            Grace period before marking late (default: 0)
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Toggle Settings */}
                    <div className="border-t pt-6">
                      <h4 className="font-medium mb-4">Additional Settings</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Single Punch Triggers Deduction</Label>
                            <p className="text-xs text-muted-foreground">
                              If enabled, days with only one punch will result in half-day deduction
                            </p>
                          </div>
                          <Switch
                            checked={configForm.single_punch_deduction}
                            onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, single_punch_deduction: checked }))}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Check Holidays</Label>
                            <p className="text-xs text-muted-foreground">
                              Skip attendance processing on company holidays
                            </p>
                          </div>
                          <Switch
                            checked={configForm.check_holidays}
                            onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, check_holidays: checked }))}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Save Button */}
                    <div className="border-t pt-6 flex justify-end">
                      <Button onClick={handleSaveConfig} disabled={configSaving}>
                        <Save className="h-4 w-4 mr-2" />
                        {configSaving ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    </div>
                    
                    {/* Last Updated Info */}
                    {config && config.updated_at && (
                      <div className="text-xs text-muted-foreground text-right">
                        Last updated: {format(new Date(config.updated_at), 'PPp')}
                        {config.updated_by_name && ` by ${config.updated_by_name}`}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Regularizations Tab */}
        <TabsContent value="regularizations" className="space-y-4">
          {/* Pending HR Approval */}
          {pendingHRRegularizations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-blue-500" />
                  Pending HR Approval ({pendingHRRegularizations.length})
                </CardTitle>
                <CardDescription>
                  Review and forward regularization requests to Super Admin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingHRRegularizations.map(req => (
                    <div key={req.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{req.employee_name}</div>
                          <div className="text-sm text-muted-foreground">Date: {req.date}</div>
                          <div className="text-sm">
                            <Badge variant="outline">{req.request_type.replace(/_/g, ' ')}</Badge>
                          </div>
                          <div className="text-sm mt-2">{req.reason}</div>
                          {req.proposed_check_in && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Proposed: {req.proposed_check_in} - {req.proposed_check_out}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setRegularizationDialog({ open: true, request: req, action: 'hr_reject' })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => setRegularizationDialog({ open: true, request: req, action: 'hr_approve' })}
                          >
                            <ArrowRight className="h-4 w-4 mr-1" />
                            Forward
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Pending Admin Approval (Super Admin only) */}
          {isSuperAdmin && pendingAdminRegularizations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-orange-500" />
                  Pending Final Approval ({pendingAdminRegularizations.length})
                </CardTitle>
                <CardDescription>
                  These requests have been approved by HR and need your final decision
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingAdminRegularizations.map(req => (
                    <div key={req.id} className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{req.employee_name}</div>
                          <div className="text-sm text-muted-foreground">Date: {req.date}</div>
                          <div className="text-sm">
                            <Badge variant="outline">{req.request_type.replace(/_/g, ' ')}</Badge>
                          </div>
                          <div className="text-sm mt-2">{req.reason}</div>
                          {req.proposed_check_in && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Proposed: {req.proposed_check_in} - {req.proposed_check_out}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            HR Approved by: {req.hr_reviewed_by_name}
                            {req.hr_comments && ` - "${req.hr_comments}"`}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setRegularizationDialog({ open: true, request: req, action: 'admin_reject' })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => setRegularizationDialog({ open: true, request: req, action: 'admin_approve' })}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* All Regularization Requests */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Regularization Requests</CardTitle>
              <CardDescription>
                History of all attendance regularization requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              {regularizationsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : regularizations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No regularization requests found
                </div>
              ) : (
                <ScrollArea className="h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {regularizations.map(req => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{req.request_type.replace(/_/g, ' ')}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                req.status === 'approved' ? 'default' :
                                req.status.includes('rejected') ? 'destructive' :
                                'secondary'
                              }
                              className={req.status === 'approved' ? 'bg-green-500' : ''}
                            >
                              {req.status.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Holidays Tab */}
        <TabsContent value="holidays" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-indigo-500" />
                    Company Holidays
                  </CardTitle>
                  <CardDescription>
                    Manage company-wide holidays. Attendance alerts and digests are automatically excluded on these days.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = new Date().getFullYear() - 2 + i;
                        return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowHolidayDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Holiday
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {holidaysLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : holidays.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No holidays configured for {selectedYear}</p>
                  <Button variant="link" onClick={() => setShowHolidayDialog(true)}>
                    Add your first holiday
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-16">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {holidays.sort((a, b) => new Date(a.date) - new Date(b.date)).map((holiday) => (
                        <TableRow key={holiday.id}>
                          <TableCell className="font-medium">
                            {formatDate(holiday.date)}
                          </TableCell>
                          <TableCell>{holiday.name}</TableCell>
                          <TableCell>
                            <Badge variant={holiday.type === 'national' ? 'default' : 'secondary'}>
                              {holiday.type || 'company'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {holiday.description || '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
          
          {/* Holiday Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-green-500" />
                  <span className="text-2xl font-bold">{holidays.filter(h => new Date(h.date) < new Date()).length}</span>
                </div>
                <p className="text-sm text-muted-foreground">Holidays Passed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <span className="text-2xl font-bold">{holidays.filter(h => new Date(h.date) >= new Date()).length}</span>
                </div>
                <p className="text-sm text-muted-foreground">Upcoming Holidays</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-500" />
                  <span className="text-2xl font-bold">{holidays.length}</span>
                </div>
                <p className="text-sm text-muted-foreground">Total for {selectedYear}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-500" />
                  <span className="text-2xl font-bold">{holidays.filter(h => h.type === 'national').length}</span>
                </div>
                <p className="text-sm text-muted-foreground">National Holidays</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Add Holiday Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Holiday</DialogTitle>
            <DialogDescription>
              Add a company-wide holiday. Attendance alerts and daily digests will be skipped on this date.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateHoliday} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="holiday-name">Holiday Name *</Label>
              <Input
                id="holiday-name"
                placeholder="e.g., Independence Day"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="holiday-date">Date *</Label>
              <Input
                id="holiday-date"
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="holiday-type">Type</Label>
              <Select value={holidayForm.type} onValueChange={(v) => setHolidayForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company Holiday</SelectItem>
                  <SelectItem value="national">National Holiday</SelectItem>
                  <SelectItem value="regional">Regional Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="holiday-description">Description (Optional)</Label>
              <Textarea
                id="holiday-description"
                placeholder="Additional details about the holiday..."
                value={holidayForm.description}
                onChange={(e) => setHolidayForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowHolidayDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={holidaySubmitting}>
                {holidaySubmitting ? 'Adding...' : 'Add Holiday'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Exception Request Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Half-Day Exception</DialogTitle>
            <DialogDescription>
              This request will be sent to Super Admin for approval.
              Upon approval, 0.5 day will be restored to employee's leave balance.
            </DialogDescription>
          </DialogHeader>
          
          {selectedDeduction && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee:</span>
                  <span className="font-medium">{selectedDeduction.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">{selectedDeduction.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hours Worked:</span>
                  <span className="font-medium">{selectedDeduction.hours_worked}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deduction:</span>
                  <span className="font-medium text-orange-600">{selectedDeduction.amount} day</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Exception *</Label>
                <Textarea
                  placeholder="Explain why this deduction should be reversed..."
                  value={exceptionReason}
                  onChange={(e) => setExceptionReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExceptionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateException} disabled={exceptionSubmitting}>
              {exceptionSubmitting ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval/Rejection Dialog */}
      <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && setApprovalDialog({ open: false, exception: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalDialog.action === 'approve' ? 'Approve Exception' : 'Reject Exception'}
            </DialogTitle>
            <DialogDescription>
              {approvalDialog.action === 'approve' 
                ? 'This will restore 0.5 day to the employee\'s leave balance.'
                : 'The deduction will remain in effect.'}
            </DialogDescription>
          </DialogHeader>
          
          {approvalDialog.exception && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee:</span>
                  <span className="font-medium">{approvalDialog.exception.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">{approvalDialog.exception.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Requested By:</span>
                  <span className="font-medium">{approvalDialog.exception.requested_by_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Reason:</span>
                  <p className="mt-1">{approvalDialog.exception.reason}</p>
                </div>
              </div>
              
              {approvalDialog.action === 'approve' ? (
                <div className="space-y-2">
                  <Label>Comments (optional)</Label>
                  <Textarea
                    placeholder="Add any comments..."
                    value={approvalComments}
                    onChange={(e) => setApprovalComments(e.target.value)}
                    rows={2}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Rejection Reason *</Label>
                  <Textarea
                    placeholder="Explain why this exception is being rejected..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog({ open: false, exception: null, action: null })}>
              Cancel
            </Button>
            {approvalDialog.action === 'approve' ? (
              <Button onClick={handleApproveException} disabled={processing} className="bg-green-600 hover:bg-green-700">
                {processing ? 'Processing...' : 'Approve & Restore Leave'}
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleRejectException} disabled={processing}>
                {processing ? 'Processing...' : 'Reject Exception'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Regularization Dialog */}
      <Dialog 
        open={regularizationDialog.open} 
        onOpenChange={(open) => !open && setRegularizationDialog({ open: false, request: null, action: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {regularizationDialog.action?.includes('approve') ? 'Approve Regularization' : 'Reject Regularization'}
            </DialogTitle>
            <DialogDescription>
              {regularizationDialog.action === 'hr_approve' && 'Forward this request to Super Admin for final approval.'}
              {regularizationDialog.action === 'admin_approve' && 'Approve and update the attendance record.'}
              {regularizationDialog.action?.includes('reject') && 'Reject this regularization request.'}
            </DialogDescription>
          </DialogHeader>
          
          {regularizationDialog.request && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee:</span>
                  <span className="font-medium">{regularizationDialog.request.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">{regularizationDialog.request.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium">{regularizationDialog.request.request_type.replace(/_/g, ' ')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Reason:</span>
                  <p className="mt-1">{regularizationDialog.request.reason}</p>
                </div>
                {regularizationDialog.request.proposed_check_in && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proposed Time:</span>
                    <span className="font-medium">
                      {regularizationDialog.request.proposed_check_in} - {regularizationDialog.request.proposed_check_out}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>
                  {regularizationDialog.action?.includes('reject') ? 'Rejection Reason *' : 'Comments (optional)'}
                </Label>
                <Textarea
                  placeholder={regularizationDialog.action?.includes('reject') 
                    ? 'Explain why this request is being rejected...' 
                    : 'Add any comments...'}
                  value={regularizationComments}
                  onChange={(e) => setRegularizationComments(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRegularizationDialog({ open: false, request: null, action: null })}
            >
              Cancel
            </Button>
            {regularizationDialog.action === 'hr_approve' && (
              <Button onClick={handleHRApproveRegularization} disabled={regularizationProcessing}>
                <ArrowRight className="h-4 w-4 mr-2" />
                {regularizationProcessing ? 'Processing...' : 'Forward to Admin'}
              </Button>
            )}
            {regularizationDialog.action === 'hr_reject' && (
              <Button variant="destructive" onClick={handleHRRejectRegularization} disabled={regularizationProcessing}>
                {regularizationProcessing ? 'Processing...' : 'Reject Request'}
              </Button>
            )}
            {regularizationDialog.action === 'admin_approve' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleAdminApproveRegularization} 
                disabled={regularizationProcessing}
              >
                <Check className="h-4 w-4 mr-2" />
                {regularizationProcessing ? 'Processing...' : 'Approve & Regularize'}
              </Button>
            )}
            {regularizationDialog.action === 'admin_reject' && (
              <Button variant="destructive" onClick={handleAdminRejectRegularization} disabled={regularizationProcessing}>
                {regularizationProcessing ? 'Processing...' : 'Reject Request'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
